import React, { useState, useEffect, useCallback } from 'react';
import EditFieldPopup from './EditFieldPopup';
import './formField.css'
import axios from "axios";
import data from "../assets/apiRoutes.json";
import notify from 'devextreme/ui/notify';
import { Button } from 'devextreme-react';
import { Toolbar, Item as ToolbarItem } from 'devextreme-react/toolbar';
import Sortable from 'devextreme-react/sortable';
import ScrollView from 'devextreme-react/scroll-view';
import Fields from './Fields';
import HiddenFieldsPopup from './HiddenFieldsPopup';
import GroupSettingsPopup from './GroupSettingsPopup';
import cloneDeep from 'lodash/cloneDeep';

// Vertical space (px) reserved above the scrollable form area for the page header + form toolbar; the
// dynamic form scrolls within the remaining viewport height rather than pushing the page.
const FORM_SCROLL_TOP_OFFSET_PX = 200;

// Role-based field access. visibleRole and editRole hold role names picked in the Edit Field popup;
// an empty or missing list means "every role". visibleRole falls back to the legacy "role" property
// so layouts saved before Visible and Editable had separate role lists keep their gating.
const hasRoleAccess = (assignedRoles, userRole) =>
  !assignedRoles || assignedRoles.length === 0 || assignedRoles.includes(userRole);

const hasVisibleRoleAccess = (field, userRole) =>
  hasRoleAccess(field.visibleRole || field.role, userRole);

const isFieldVisibleForUser = (field, userRole) =>
  field.visible === true && hasVisibleRoleAccess(field, userRole);

// Resolves a field for the signed-in user before it is rendered. When editRole excludes the user the
// field becomes read-only and its Required rule is dropped: a field the user cannot edit can never be
// filled in, so validating it would block the form with no way to satisfy the rule. Fields excluded by
// visibleRole are never rendered at all, so their Required rule never registers either - which leaves
// Required applying only to fields the user can both see and edit.
const applyRoleAccess = (field, userRole) =>
  hasRoleAccess(field.editRole, userRole) ? field : { ...field, readOnly: true, required: false };

// onFormLoad defaults to a no-op: fetchData calls it from a `finally`, so a consumer that has no
// post-load work to do would otherwise throw "onFormLoad is not a function" and abort the render of
// its whole form. Pages that pass one still win — this only covers the omitted case.
const FormField = ({ currrentUser, layoutId, layoutName, defaultFormSchema, formFields, setformFields, handleFieldValueChange, DYNAMICFORMSETTING, formRefs, onFormLoad = () => {}, noSpacingBeforeFirstRow, handleSelectedItemChange, handleFocusItemIn, hasOnlyOneColumn }) => {
  const [popupVisible, setPopupVisible] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [discard, setDiscard] = useState(false);
  const [formSchema, setFormSchema] = useState([]);
  const [hiddenFields, SethiddenFields] = useState([]);
  const [hiddenFieldPopupVisible, setHiddenFieldPopupVisible] = useState(false);
  const [groupPopupVisible, setGroupPopupVisible] = useState(false);
  const [isMergeSchema, setIsMergeSchema] = useState(false);

  const onTaskDrop = useCallback(
    ({
      fromData, toData, fromIndex, toIndex,
    }) => {
      if (fromData === 'hiddenGroup' && toData === 'hiddenGroup') { return; }

      else if (fromData === 'hiddenGroup' && toData !== 'hiddenGroup') {
        const newHiddenFields = [...hiddenFields];
        const updatedLists = [...formSchema];
        const item = newHiddenFields[fromIndex];
        item.visible = true;
        //const newHiddenField = removeItem(newHiddenFields, fromIndex);

        // Remove field from hiddenFields
        // Ensure the field is removed from any group it might be in
        updatedLists.forEach(group => {
          const fieldIndex = group.fields.findIndex(field => field.name === item.name);
          if (fieldIndex !== -1) {
            group.fields.splice(fieldIndex, 1);
          }
        });

        // Make the field visible and add it to the target group
        updatedLists[toData].fields = insertItem(updatedLists[toData].fields, item, toIndex);

        // Update the states
        setFormSchema(updatedLists);
        SethiddenFields(newHiddenFields);
        setIsMergeSchema(!isMergeSchema);
      } else if (toData === 'hiddenGroup' && fromData !== 'hiddenGroup') {
        const newHiddenFields = [...hiddenFields];
        const updatedLists = [...formSchema];
        const item = updatedLists[fromData].fields[fromIndex];
        item.visible = false;
        // Remove field from the source group
        //updatedLists[fromData].fields = removeItem(updatedLists[fromData].fields, fromIndex);

        // Make the field hidden and add it to the hiddenFields
        newHiddenFields.splice(toIndex, 0, item);

        // Update the states
        setFormSchema(updatedLists);
        SethiddenFields(newHiddenFields);
        setIsMergeSchema(!isMergeSchema);
      }
      else if (toData !== 'hiddenGroup' && fromData !== 'hiddenGroup') {
        const updatedLists = [...formSchema];
        const item = updatedLists[fromData].fields[fromIndex];
        updatedLists[fromData].fields = removeItem(updatedLists[fromData].fields, fromIndex);
        updatedLists[toData].fields = insertItem(updatedLists[toData].fields, item, toIndex);
        setFormSchema(updatedLists);
      }
    },
    [formSchema],
  );
  const onListReorder = useCallback(({ fromIndex, toIndex }) => {
    setFormSchema((state) => reorderItem(state, fromIndex, toIndex));
    // newSchema=reorderItem(newSchema, fromIndex, toIndex);
    //setFormSchema(newSchema)
  }, []);

  function removeItem(array, removeIdx) {
    return array.filter((_, idx) => idx !== removeIdx);
  }
  function insertItem(array, item, insertIdx) {
    const newArray = [...array];
    newArray.splice(insertIdx, 0, item);
    return newArray;
  }
  function reorderItem(array, fromIdx, toIdx) {
    const item = array[fromIdx];
    const result = removeItem(array, fromIdx);
    return insertItem(result, item, toIdx);
  }
  const mergeSchemas = (formSchemaDatas) => {
    if(!formSchemaDatas || formSchemaDatas.length===0)return;
    const formSchemaData = cloneDeep(formSchemaDatas);
    // Create a set of all field names present in any group of formSchema
    const formSchemaFieldNames = new Set();
    if (formSchemaData) {
      formSchemaData.forEach(group => {
        if (group) {
          group.fields.forEach(field => {
            formSchemaFieldNames.add(field.name);
          });
        }
      });
    }

    // Iterate through each group in defaultFormSchema
    defaultFormSchema.forEach(defaultGroup => {
      const { groupId, fields: defaultFields } = defaultGroup;

      // Find the corresponding group in formSchemaData
      let formGroup = formSchemaData.find(group => group.groupId === groupId);

      // If the group exists, add the fields that do not exist already
      if (formGroup) {
        defaultFields.forEach(defaultField => {
          if (!formSchemaFieldNames.has(defaultField.name)) {
            formGroup.fields.push(defaultField);
          }
        });
      }
    });
    //lookups layout mergings

    formSchemaData.forEach(group => {
      if (group) {
        group.fields.forEach(field => {
          if (field.type === 'lookup') {
            const targetField = getLookupField(field.name);
            if (targetField?.layout) {
              field.layout = { ...targetField.layout };
             // field.onCustomItemCreatingComponent = { ...targetField.onCustomItemCreatingComponent };

            } else if (targetField && targetField.type !== 'lookup') {
              field.type = targetField.type;
            }
          }
        });
      }
    });


    let inVisibleFields = getAllInvisibleFields(formSchemaData);
    SethiddenFields(inVisibleFields);
    let sortedData = sortFormSchemaFieldsByVisibility(formSchemaData)
    setFormSchema(sortedData);
  }
  const getAllInvisibleFields = (formSchemas) => {
    return formSchemas.reduce((acc, group) => {
      // Fields hidden by role are left out entirely - the Hidden Fields popup lists only the fields
      // this user hid from the form themselves and whose Visible Role still grants them access.
      const invisibleFields = group.fields.filter(field => field.visible === false
        && hasVisibleRoleAccess(field, currrentUser.profile.role));
      return [...acc, ...invisibleFields];
    }, []);
  };
  const sortFieldsByVisibility = (fields) => {
    return fields.sort((a, b) => (a.visible === b.visible) ? 0 : a.visible ? -1 : 1);
  };
  const getLookupField = (name) => {
    for (const group of defaultFormSchema) {
      if (group) {
        const field = group.fields.find(field => field.name === name);
        if (field) {
          return field;
        }
      }
    }
  }

  const sortFormSchemaFieldsByVisibility = (formSchemas) => {
    return formSchemas.map(group => {
      return {
        ...group,
        fields: sortFieldsByVisibility(group.fields)
      };
    });
  };
  const toggleEditMode = () => {
    setEditMode(!editMode);
  };

  const cancelEditing = () => {
    setDiscard(!discard);
    setEditMode(false);
    setHiddenFieldPopupVisible(false);
  }

  const fetchData = async () => {
    try {
      // const { gridInfo } = data; // Assuming gridInfo is part of your data object
      const response = await axios.get(
        `${window._env_.REACT_APP_APIS}/${data.DynamicForm.GetFormLayout}`,
        {
          params: {
            key: layoutId,
            name: layoutName
          },
          headers: {
            'Content-Type': 'application/json',
            'access_token': currrentUser.access_token,
            'Authorization': `Bearer ${currrentUser.access_token}`,
          },
        }
      );

      // Ensure proper parsing of response data
      let formSchemaData = response.data;
      if (typeof formSchemaData === 'string') {
        formSchemaData = JSON.parse(formSchemaData);
      }
      // Update state with the fetched form schema data
      if (formSchemaData && formSchemaData !== null && formSchemaData.length > 0)
        mergeSchemas(formSchemaData);
      else
        mergeSchemas(defaultFormSchema);
    } catch (err) {
      console.log('Error in loading form', err);
      mergeSchemas(defaultFormSchema);
    }
    finally {
      onFormLoad();
    }
  };
  const removeLayouts = (formSchema) => {
    const formSchemaData = cloneDeep(formSchema);

    formSchemaData.forEach(group => {
      if (group?.fields) {
        group.fields.forEach(field => {
          if (field.type === 'lookup') {
            field.layout = null;
           // field.onCustomItemCreatingComponent = null;
          }
        });
      }
    });
    return formSchemaData;
  };
  const updateSchema = () => {
    let removedLayouts=removeLayouts(formSchema);
    const requestOptions = {
      key: layoutId,
      isDefault: true,
      name: layoutName,
      state: removedLayouts ? JSON.stringify(removedLayouts) : null
    };
    return axios
      .post(`${window._env_.REACT_APP_APIS}/${data.DynamicForm.SaveUpdateFormLayout}`, requestOptions, {
        headers: {
          "Content-Type": "application/json",
          access_token: currrentUser.access_token,
          Authorization: `Bearer ${currrentUser.access_token}`,
        },
      })
      .then(response => {
        notify(
          {
            message: 'Form layout saved successfully',
            width: 300,
          },
          'success',
          3000,
        );
        setEditMode(!editMode);
        return response;
      })
      .catch(() => { throw 'Layout Save/Update Error'; });
  }
  const handleGroupUpdate = (updatedGroup) => {
    const newSdchem = [...formSchema];
    newSdchem.map(group => {
      if (group.groupId === selectedGroup.groupId) {
        // Check if the label has changed
        if (group.label !== updatedGroup.label) {
          group.label = updatedGroup.label
        }
      }
    });
    setFormSchema(newSdchem);
    setGroupPopupVisible(false)
  }
  const handleFieldUpdate = (updatedField) => {

    const updatedSchema = formSchema.map(group => {
      if (group.groupId === selectedField.groupId) {
        const updatedFields = group.fields.map(field => {
          if (field.name === selectedField.name) {
            // Check if the label has changed
            if (field.label !== updatedField.label) {
              return { ...updatedField, isLabelChanged: true };
            }
            return updatedField;
          }
          return field;
        });
        return { ...group, fields: updatedFields };
      }
      return group;
    });
    setFormSchema(updatedSchema);
    setPopupVisible(false);
  };

  const handleFieldChange = useCallback((name, value, e) => {
    setformFields(prevState => ({
      ...prevState,
      [name]: value,
    }));
    if (typeof handleFieldValueChange === 'function') {
      handleFieldValueChange(name, value, e);
    }
  }, []);

  const handleSelectionChange = useCallback((name, e) => {
    if (typeof handleSelectedItemChange === 'function') {
    handleSelectedItemChange(name, e);
    }
  }, []);
  const handleFocusIn = useCallback((name, e) => { 
    if (typeof handleFocusItemIn === 'function') {
      handleFocusItemIn(name, e);
    }
  }, []);

  const fieldRef = (el) => {
    if (el) {
      if (formRefs)
        formRefs.current["div" + el.attributes['data-fieldname'].value] = el;
    }
  };

  useEffect(() => {
    fetchData();

  }, [discard]);
  useEffect(() => {
    mergeSchemas(formSchema);
  }, [isMergeSchema]);

  const isGroupFieldsExists = (fields) => {
    let isEmpty = fields.filter(f => f.visible);
    let isExsts = isEmpty.length > 0 && fields.length > 0;
    return isExsts;
  };
  return (
    <>
      <div id="kanban">
      <div style={{ maxHeight: `calc(100vh - ${FORM_SCROLL_TOP_OFFSET_PX}px)`, overflowY: 'auto', overflowX: 'auto' }}>
        <div className='row' style={{ display: 'flex', alignItems: 'flex-start' }}>
          <div style={hasOnlyOneColumn ? {} : { flexBasis: '97%' }}>
            <Sortable
              className={editMode ? `sortable-lists}` : 'flex-container'}
              itemOrientation="horizontal"
              handle={editMode ? ".list-title-move" : ".list-title"}
              onReorder={editMode ? onListReorder : null}
            >
              {formSchema && formSchema.map((group, groupIndex) => (
                group.label !== null && group.label !== "" && group.label !== undefined ? (
                  <fieldset className={editMode ? (hasOnlyOneColumn ? `list` : `list col-md-4 col-fieldset`) : (hasOnlyOneColumn ? `list` : `list col-md-4 group-col col-fieldset`)} key={groupIndex} >
                    <legend className="list-title" >{group.label}</legend>
                    <div className={editMode ? `droppable-container` : ''} key={groupIndex}>
                      <div className={editMode ? "list-title-move" : "list-title"} key={groupIndex}>
                        {editMode && (
                          <div className="header-content" key={groupIndex}>
                            <span>&#9776;</span>
                            <Button icon='fa fa-gear' onClick={() => {
                              setSelectedGroup(group);
                              setGroupPopupVisible(true);
                            }} className="gear-button" />
                          </div>
                        )}
                      </div>

                      <Sortable
                        className="sortable-cards"
                        group="cardsGroup"
                        data={groupIndex}
                        onReorder={editMode ? onTaskDrop : null}
                        onAdd={editMode ? onTaskDrop : null}
                      >
                        {!isGroupFieldsExists(group.fields) && editMode ? <div className="empty-placeholder">Drop fields here</div> :
                          group.fields.filter(field => isFieldVisibleForUser(field, currrentUser.profile.role)).map((field, idx) => (
                            <div className="field-container" key={idx + field.name}>
                              <div key={idx + field.name} data-fieldname={field.name} ref={fieldRef} className={editMode ? 'form-fields-move' : (noSpacingBeforeFirstRow && idx == 0 ? '' : 'form-fields')}>
                                {Fields(applyRoleAccess(field, currrentUser.profile.role), formFields, handleFieldChange, false, editMode, formRefs, handleSelectionChange, handleFocusIn)}
                              </div>
                              {editMode && (
                                <Button
                                  className="edit-button"
                                  icon='edit'
                                  onClick={() => {
                                    setPopupVisible(true);
                                    setSelectedField({ ...field, groupId: group.groupId });
                                  }}
                                />
                              )}
                            </div>
                          ))}
                      </Sortable>
                    </div>
                  </fieldset>
                ) :
                  (
                    <div className={editMode ? `list col-md-4` : (hasOnlyOneColumn ? 'list' : 'list col-md-4 group-col')} key={groupIndex} >
                      <div className={editMode ? `droppable-container` : ''} key={groupIndex}>
                        <div className={editMode ? "list-title-move" : "list-title"} key={groupIndex}>
                          {editMode && (
                            <div className="header-content" key={groupIndex}>
                              <span>&#9776;</span>
                              <Button icon='fa fa-gear' onClick={() => {
                                setSelectedGroup(group);
                                setGroupPopupVisible(true);
                              }} className="gear-button" />
                            </div>
                          )}
                        </div>

                        <Sortable
                          className="sortable-cards"
                          group="cardsGroup"
                          data={groupIndex}
                          onReorder={editMode ? onTaskDrop : null}
                          onAdd={editMode ? onTaskDrop : null}
                        >
                          {!isGroupFieldsExists(group.fields) && editMode ? <div className="empty-placeholder">Drop fields here</div> :
                            group.fields.filter(field => isFieldVisibleForUser(field, currrentUser.profile.role)).map((field, idx) => (
                              <div className="field-container " key={idx + field.name}>
                                <div key={idx + field.name} data-fieldname={field.name} ref={fieldRef} className={editMode ? 'form-fields-move' : (noSpacingBeforeFirstRow && idx == 0 ? '' : 'form-fields')}>
                                  {Fields(applyRoleAccess(field, currrentUser.profile.role), formFields, handleFieldChange, false, editMode, formRefs, handleSelectedItemChange, handleFocusItemIn)}
                                </div>
                                {editMode && (
                                  <Button
                                    className="edit-button"
                                    icon='edit'
                                    onClick={() => {
                                      setPopupVisible(true);
                                      setSelectedField({ ...field, groupId: group.groupId });
                                    }}
                                  />
                                )}
                              </div>
                            ))}
                        </Sortable>
                      </div>
                    </div>

                  )
              ))}
            </Sortable>
          </div>
          {DYNAMICFORMSETTING?.toLowerCase() === "yes" && (
            <div style={{ flexBasis: '3%', display: 'flex', flexDirection: 'column' }}>
              {DYNAMICFORMSETTING?.toLowerCase() === "yes" && !editMode && (
                <div className='row' style={{ justifyContent: "right" }}>
                  <Button icon='fa fa-pencil-square' onClick={toggleEditMode} />
                </div>
              )}
              {editMode && (
                <>
                  <div className='row'>
                    <Button icon='save' onClick={updateSchema} />
                  </div>
                  <div className='row'>
                    <Button icon='fieldchooser' className='edit-buttons' onClick={() => setHiddenFieldPopupVisible(true)} />
                  </div>
                  <div className='row'>
                    <Button icon='fa fa-times-circle' className='edit-buttons' onClick={cancelEditing} />
                  </div>
                </>
              )}
            </div>


          )}
        </div>
      </div>
      </div>

      {hiddenFieldPopupVisible && (
        <HiddenFieldsPopup visible={hiddenFieldPopupVisible}
          onClose={() => setHiddenFieldPopupVisible(false)}
          hiddenFields={hiddenFields}
          formFields={formFields}
          handleFieldChange={handleFieldValueChange}
          currrentUser={currrentUser}
          editMode={editMode}
          onTaskDrop={onTaskDrop} />
      )}
      {popupVisible && selectedField && (
        <EditFieldPopup
          field={selectedField}
          visible={popupVisible}
          onClose={() => setPopupVisible(false)}
          handleFieldUpdate={handleFieldUpdate}
        />
      )}
      {groupPopupVisible && (
        <GroupSettingsPopup
          group={selectedGroup}
          visible={groupPopupVisible}
          onClose={() => setGroupPopupVisible(false)}
          handleGroupUpdate={handleGroupUpdate}
        />
      )}

    </>
  );
};

export default FormField;
